// SPDX-FileCopyrightText: Copyright 2025 Dash0 Inc.

"use client";

import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import clsx from "clsx";
import { Heading } from "@/hub/pages/integration-detail-page/integration-detail-page";

export default function SideBar({
  content,
  authors,
  title,
  headings,
}: {
  content?: string;
  authors?: Author[];
  title?: string;
  blog?: boolean;
  bannerActive?: boolean;
  headings: Heading[];
}) {
  const [activeSection, setActiveSection] = useState<{
    key: string;
    heading: "h1" | "h2";
  }>();
  const sticky = useRef<HTMLDivElement>(null);
  const sidebarWrapper = useRef<HTMLDivElement>(null);
  const lastHighlightedId = useRef<string | null>(null);

  // Set first element as active on mount
  useEffect(() => {
    if (headings.length > 0) {
      setActiveSection({
        key: headings[0].id,
        heading: "h1",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only initialise active section on mount
  }, []);

  // Scroll to a section based on its ID and set it as active
  function scrollToSection(id: string, heading: "h1" | "h2") {
    // If already active, skip
    if (activeSection?.key === id && activeSection?.heading === heading) return;

    const element = document.getElementById(id);

    if (element) {
      const rect = element.getBoundingClientRect();
      const yOffset = -96; // Adjust this for your desired offset

      window.scrollTo({
        top: rect.top + window.pageYOffset + yOffset,
        behavior: "smooth",
      });
    }

    setActiveSection({
      key: id,
      heading,
    });
  }

  useEffect(() => {
    const handleScroll = () => {
      const threshold = 96 + 2;
      const positions: {
        top: number;
        bottom: number;
        id: string;
        type: "main" | "child";
      }[] = [];

      headings.forEach((heading, index) => {
        const mainElement = document.getElementById(heading.id);
        if (!mainElement) return;

        const mainTop = Math.floor(mainElement.getBoundingClientRect().top);
        let mainBottom = document.body.scrollHeight;

        if (heading.childHeadings?.length) {
          const firstChild = document.getElementById(
            heading.childHeadings[0].id,
          );
          if (firstChild)
            mainBottom = Math.floor(firstChild.getBoundingClientRect().top) - 1;
        } else if (headings[index + 1]) {
          const nextMain = document.getElementById(headings[index + 1].id);
          if (nextMain)
            mainBottom = Math.floor(nextMain.getBoundingClientRect().top) - 1;
        }

        positions.push({
          top: mainTop,
          bottom: mainBottom,
          id: heading.id,
          type: "main",
        });

        heading.childHeadings?.forEach((child, childIndex) => {
          const childEl = document.getElementById(child.id);
          if (!childEl) return;

          const childTop = Math.floor(childEl.getBoundingClientRect().top);
          let childBottom = document.body.scrollHeight;

          const nextChild = heading.childHeadings[childIndex + 1];
          if (nextChild) {
            const nextChildEl = document.getElementById(nextChild.id);
            if (nextChildEl)
              childBottom =
                Math.floor(nextChildEl.getBoundingClientRect().top) - 1;
          } else if (headings[index + 1]) {
            const nextMainEl = document.getElementById(headings[index + 1].id);
            if (nextMainEl)
              childBottom =
                Math.floor(nextMainEl.getBoundingClientRect().top) - 1;
          }

          positions.push({
            top: childTop,
            bottom: childBottom,
            id: child.id,
            type: "child",
          });
        });
      });

      for (const pos of positions) {
        if (pos.top <= threshold && pos.bottom > threshold) {
          if (lastHighlightedId.current !== pos.id) {
            lastHighlightedId.current = pos.id;
            setActiveSection({
              key: pos.id,
              heading: pos.type === "main" ? "h1" : "h2",
            });
          }
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [headings]);

  return (
    <div
      className="sticky hidden lg:flex top-[75px] scroll-my-6 flex-col left-0 sidebar-container h-fit!"
      ref={sticky}
    >
      <div className="w-full flex-1 relative">
        {headings && headings.length > 1 && (
          <div
            className="overflow-y-auto h-fit pb-16 leading-none hide-scrollbar pl-4"
            ref={sidebarWrapper}
          >
            {title && <span className="blog-tag pb-4 block">{title}</span>}
            <ul id="sidebar-list" className="hidden lg:flex flex-col relative">
              {headings.map((heading) => (
                <React.Fragment key={heading.id}>
                  <Link
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(heading.id, "h1");
                    }}
                    href={"#" + heading.id}
                    className={clsx(
                      "relative blog-tag transition-colors leading-normal duration-100 w-full font-medium",
                      activeSection && activeSection.key === heading.id
                        ? "text-white"
                        : "text-neutral-400 hover:text-white",
                      "py-2",
                    )}
                  >
                    {activeSection && activeSection.key === heading.id && (
                      <div
                        className={`bg-red ring-[3px] ring-grey-950 h-2 w-2 aspect-square rounded-full absolute transition-all duration-100 -left-4 top-[11px]`}
                      />
                    )}
                    {heading.label}
                  </Link>
                  {heading.childHeadings?.map((child) => (
                    <Link
                      key={child.id}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection(child.id, "h2");
                      }}
                      href={"#" + child.id}
                      className={clsx(
                        "relative blog-tag transition-colors leading-normal duration-100 w-full pl-4 font-medium",
                        activeSection && activeSection.key === child.id
                          ? "text-white"
                          : "text-neutral-400 hover:text-white",
                        "py-2",
                      )}
                    >
                      {activeSection && activeSection.key === child.id && (
                        <div
                          className={`bg-red ring-[3px] ring-grey-950 h-2 w-2 aspect-square rounded-full absolute transition-all duration-100 left-0 top-[11px]`}
                        />
                      )}
                      {child.label}
                    </Link>
                  ))}
                </React.Fragment>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
